import { resolveVersionItems, type VersionsResponse } from "../../taskpane/agreement";
import type { CollaborateAgreementVersion } from "../types/collaborateThread";

function parseVersionSortKey(versionLabel: string): number {
  const match = /^(\d+)/.exec(versionLabel.trim());
  return match ? Number.parseInt(match[1], 10) : 0;
}

function formatVersionLabel(versionLabel: string): string {
  const trimmed = versionLabel.trim();
  if (!trimmed) {
    return "v?";
  }
  const hasNumericPrefix = /^\d+/.test(trimmed);
  if (trimmed.includes("-clean")) {
    const sortKey = parseVersionSortKey(trimmed);
    return hasNumericPrefix ? `v${sortKey} clean` : trimmed;
  }
  const sortKey = parseVersionSortKey(trimmed);
  return hasNumericPrefix ? `v${sortKey}` : trimmed;
}

/** Map agreement version history to collaborate version options (prefers `items[]`). */
export function mapAgreementVersionsForCollaborate(
  versionsData: VersionsResponse | null,
): CollaborateAgreementVersion[] {
  const items = resolveVersionItems(versionsData);
  if (items.length > 0) {
    const byId = new Map<string, CollaborateAgreementVersion>();
    const currentId = versionsData?.current_version_id?.trim();
    for (const row of items) {
      const id = (row.id || row.version_id || "").trim();
      if (!id) continue;
      const label = row.display_label?.trim() || formatVersionLabel(String(row.version));
      const existing = byId.get(id);
      if (!existing || row.id === currentId) {
        byId.set(id, { id, version: row.version, label });
      }
    }
    return Array.from(byId.values()).sort((a, b) => b.version - a.version);
  }

  if (!versionsData?.versions?.length) {
    return [];
  }

  const byId = new Map<string, CollaborateAgreementVersion>();
  for (const row of versionsData.versions) {
    const id = row.version_id?.trim();
    if (!id) {
      continue;
    }
    const sortKey = parseVersionSortKey(row.version);
    const label = row.display_label?.trim() || formatVersionLabel(row.version);
    const existing = byId.get(id);
    if (!existing || row.version === versionsData.current_version) {
      byId.set(id, { id, version: sortKey, label });
    }
  }

  return Array.from(byId.values()).sort((a, b) => b.version - a.version);
}

/** Default comment target: stamped document version id, else current version label. */
export function resolveDocumentComposerVersionId(
  versions: CollaborateAgreementVersion[],
  documentVersionId: string | null,
  currentVersionLabel: string | null,
): string {
  const docId = documentVersionId?.trim();
  if (docId && versions.some((version) => version.id === docId)) {
    return docId;
  }

  const currentLabel = currentVersionLabel?.trim();
  if (currentLabel) {
    const exact = versionsDataVersionMatch(versions, currentLabel);
    if (exact) {
      return exact.id;
    }
    const sortKey = parseVersionSortKey(currentLabel);
    const byNumber = versions.find((version) => version.version === sortKey);
    if (byNumber) {
      return byNumber.id;
    }
  }

  return versions[0]?.id ?? docId ?? "";
}

function versionsDataVersionMatch(
  versions: CollaborateAgreementVersion[],
  currentVersionLabel: string,
): CollaborateAgreementVersion | undefined {
  const normalized = formatVersionLabel(currentVersionLabel);
  return versions.find(
    (version) =>
      version.label === normalized || version.label === currentVersionLabel.trim(),
  );
}
